import { createReducer, on } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ViewStateSequenceSerializer } from '@src/suite/common/data-models/serializers/serializers';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { MultipleFeedViewStateMode, ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import {
    isFilteredViewStateRecord,
    isMultiFeedViewStateRecord,
    isPublicationViewStateRecord,
    isRSSSFeedViewStateRecord,
    isSingleFeedViewStateRecord,
    isSingleViewStateRecord,
    isTopicViewStateRecord,
    isTwitterFeedViewStateRecord,
} from '@src/suite/common/utils/viewstate-utils';

import { CreateViewStateActions } from './createviewstate.actions';
import { CreateViewStateState } from './createviewstate.models';

export const initialState: CreateViewStateState = {
    isLoading: false,
    isCopy: false,

    currentTitle: '',
    viewState: null,
    inEditMode: false,
    selectedGroup: null,
    selectedSection: null,
    selectedItem: null,

    inAddContentSourceMode: false,
    inBrowseMode: false,
    inConfigureMode: false,
    multipleFeedViewState: null,
    multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
    multipleFeedViewStateSequence: [],
    selectedViewStates: [],

    storedViewStates: [],
    appViewStates: [],
};

function handleUpdateViewState(state: CreateViewStateState, vs: StorytellingViewStateRecord): CreateViewStateState {
    return {
        ...state,
        viewState: { ...vs },
    };
}

export const createViewStateReducer = createReducer(
    initialState,
    on(CreateViewStateActions.resetState, (_): CreateViewStateState => {
        return { ...initialState };
    }),
    on(CreateViewStateActions.initializeViewState, (state, _): CreateViewStateState => {
        if (state.inConfigureMode) {
            return {
                ...state,
                viewState: null,
                isLoading: true,
            };
        }
        return {
            ...state,
            inAddContentSourceMode: false,
            inBrowseMode: false,
            inConfigureMode: false,
            multipleFeedViewState: null,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
            viewState: null,
            isLoading: true,
        };
    }),
    on(CreateViewStateActions.initializeViewStateSuccess, (state, action): CreateViewStateState => {
        let vs: StorytellingViewStateRecord = {
            Type: ObjectTypes.PSTVIEWSTATE,
            PSTViewStateType: action.vs ? action.vs.PSTViewStateType : action.vstype,
        };

        if (action.title) vs.Title = action.title;

        if (action.copy && action.vs) {
            vs = { ...action.vs };
            vs.Title = vs.Title + ' (Copy)';
        }

        if (action.edit) {
            vs = { ...action.vs };
        }

        if ((isSingleViewStateRecord(vs) || isPublicationViewStateRecord(vs) || isMultiFeedViewStateRecord(vs)) && action.layout) {
            const value = vs?.PSTViewStateLayout?.Value;
            const layout = {
                ...action?.layout,
                Value: value ?? action?.layout?.Default,
                Label: action?.layout?.Options?.find(opt => opt.id === value)?.title ?? action?.layout?.Label,
            };
            vs.PSTViewStateLayout = layout;
        }

        if ((isSingleViewStateRecord(vs) || isMultiFeedViewStateRecord(vs)) && action.groupBy) {
            const value = (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord)?.PSTViewStateGrouping?.Value;
            const groupby = {
                ...action?.groupBy,
                Value: value ?? action?.groupBy?.Default,
                Label: action?.groupBy?.Options?.find(opt => opt.id === value)?.title ?? action?.groupBy?.Label,
            };
            (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord).PSTViewStateGrouping = groupby;
        }

        if ((isSingleViewStateRecord(vs) || isMultiFeedViewStateRecord(vs)) && action.sortBy) {
            const value = (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord)?.PSTViewStateSorting?.Value;
            const sortby = {
                ...action?.sortBy,
                Value: value ?? action?.sortBy?.Default,
                Label: action?.sortBy?.Options?.find(opt => opt.id === value)?.title ?? action?.sortBy?.Label,
            };
            (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord).PSTViewStateSorting = sortby;
        }

        // Set default filter state to open and order to desc for new views (not edit or copy)
        if ((isSingleViewStateRecord(vs) || isMultiFeedViewStateRecord(vs)) && !action.edit && !action.copy) {
            vs.PSTViewStateFilterState = true;
            (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord).PSTViewStateGroupSortOrder =
                (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord)?.PSTViewStateGroupSortOrder ?? ViewStateSortOders.DESC;
            (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord).PSTViewStateSortOrder =
                (vs as StorytellingSingleViewStateRecord | StorytellingMultiFeedViewStateRecord)?.PSTViewStateSortOrder ?? ViewStateSortOders.DESC;
        }

        if (isFilteredViewStateRecord(vs) && action.filter) {
            const filter = { ...action.filter };
            (vs as StorytellingSingleViewStateRecord).PSTViewStateFilter = (vs as StorytellingSingleViewStateRecord)?.PSTViewStateFilter ?? filter;
        }

        if (isMultiFeedViewStateRecord(vs) && action.sequence) {
            let sequence = { ...action.sequence };
            if (action.edit) {
                const selected = (vs as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence?.ViewStates?.filter(vs => vs.Selected)?.map(vs => vs.Guid);
                sequence = {
                    ...sequence,
                    ViewStates: sequence.ViewStates.map(vs => {
                        return {
                            ...vs,
                            Selected: selected.includes(vs.Guid),
                        };
                    }),
                };
            }
            (vs as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence = sequence;
        }

        return {
            ...state,
            currentTitle: vs.Title,
            viewState: vs,
            isLoading: false,
            isCopy: action.copy,
            inEditMode: action.edit,
            selectedGroup: action.group,
            selectedSection: action.section,
            selectedItem: action.item,
        };
    }),
    on(CreateViewStateActions.initializeViewStateError, (state, _): CreateViewStateState => {
        return {
            ...state,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.enterBrowseModeSuccess, (state, _): CreateViewStateState => {
        if (isMultiFeedViewStateRecord(state.viewState) || isMultiFeedViewStateRecord(state.multipleFeedViewState)) {
            const viewstates = state.appViewStates;

            let vs: StorytellingMultiFeedViewStateRecord = {
                Type: ObjectTypes.PSTVIEWSTATE,
                PSTViewStateType: ViewStateTypes.MULTIPLEFEED,
            };

            if (state.multipleFeedViewState) {
                vs = { ...state.multipleFeedViewState };
            } else {
                vs = { ...(state.viewState as StorytellingMultiFeedViewStateRecord) };
            }

            vs.PSTViewStateSequence = ViewStateSequenceSerializer.instance.initialize(viewstates, vs.PSTViewStateSequence);

            return {
                ...state,
                viewState: vs,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.BROWSE,
                inBrowseMode: true,
                inConfigureMode: false,
                inAddContentSourceMode: true,
                isLoading: false,
            };
        }

        return {
            ...state,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.enterBrowseMode, (state, _): CreateViewStateState => {
        if (isMultiFeedViewStateRecord(state.viewState)) {
            const vs = state.viewState as StorytellingMultiFeedViewStateRecord;
            const selected = vs.PSTViewStateSequence?.ViewStates?.filter(item => item.Selected).map(item => item.Guid);

            return {
                ...state,
                multipleFeedViewStateSequence: selected,
                isLoading: true,
            };
        }

        return {
            ...state,
            isLoading: true,
        };
    }),
    on(CreateViewStateActions.enterConfigureModeSuccess, (state, _): CreateViewStateState => {
        const viewState: StorytellingRSSViewStateRecord = {
            Title: null,
            PSTViewStateType: ViewStateTypes.RSSFEED,
        };
        const multiFeedViewState = state.viewState as StorytellingMultiFeedViewStateRecord;

        return {
            ...state,
            viewState: viewState,
            currentTitle: viewState.Title,
            multipleFeedViewState: multiFeedViewState,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.CONFIGURE,
            inBrowseMode: false,
            inConfigureMode: true,
            inAddContentSourceMode: true,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.cancelAddViewStates, (state, _): CreateViewStateState => {
        if (state.inConfigureMode) {
            const multiFeedViewState = state.multipleFeedViewState as StorytellingMultiFeedViewStateRecord;
            return {
                ...state,
                viewState: multiFeedViewState,
                currentTitle: multiFeedViewState.Title,
                multipleFeedViewState: null,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }
        if (state.inBrowseMode) {
            let newViewState: StorytellingMultiFeedViewStateRecord = {
                PSTViewStateType: ViewStateTypes.MULTIPLEFEED,
            };

            const vs = state.viewState as StorytellingMultiFeedViewStateRecord;
            const sequence = { ...vs.PSTViewStateSequence };
            sequence.ViewStates.forEach(item => {
                const vsItem = { ...item };
                if (state.multipleFeedViewStateSequence.find(guid => guid === item.Guid)) {
                    vsItem.Selected = true;
                } else {
                    vsItem.Selected = false;
                }
            });

            newViewState = { ...vs };
            newViewState.PSTViewStateSequence = sequence;

            return {
                ...state,
                viewState: newViewState,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.saveViewStateSuccess, (state, action): CreateViewStateState => {
        return {
            ...state,
            viewState: action.vs,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.saveAddViewStatesSuccess, (state, action): CreateViewStateState => {
        if (state.inConfigureMode && state.multipleFeedViewState) {
            const viewState = state.multipleFeedViewState as StorytellingMultiFeedViewStateRecord;

            const viewStateSequence = { ...(viewState as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence };
            viewStateSequence.ViewStates.push({
                Guid: action.vs.Guid,
                Title: action.vs.Title,
                Type: action.vs.PSTViewStateType,
                Selected: true,
            });

            return {
                ...state,
                viewState: viewState,
                multipleFeedViewState: null,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }

        if (state.inBrowseMode) {
            const currentSequence = (state.viewState as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence;
            const viewState = {
                ...(state.viewState as StorytellingMultiFeedViewStateRecord),
                PSTViewStateSequence: {
                    ...currentSequence,
                    ViewStates: currentSequence.ViewStates.map(item => {
                        if (state.selectedViewStates.find(guid => guid === item.Guid)) {
                            return { ...item, Selected: true };
                        } else {
                            return { ...item, Selected: false };
                        }
                    }),
                },
            };

            return {
                ...state,
                viewState: viewState,
                multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
                inBrowseMode: false,
                inConfigureMode: false,
                inAddContentSourceMode: false,
                isLoading: false,
            };
        }

        return {
            ...state,
            multipleFeedViewStateMode: MultipleFeedViewStateMode.NONE,
            inBrowseMode: false,
            inConfigureMode: false,
            inAddContentSourceMode: false,
            isLoading: false,
        };
    }),
    on(CreateViewStateActions.updateViewStateTitle, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const vs: StorytellingViewStateRecord = {
                ...state.viewState,
                PSTViewStateType: state.viewState.PSTViewStateType,
                Title: action.title,
            };
            return {
                ...state,
                currentTitle: action.title,
                viewState: vs,
            };
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateLayout, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const value = state.viewState?.PSTViewStateLayout?.Options.find(item => item?.id === action?.layout);
            const vs = {
                ...state.viewState,
                PSTViewStateLayout: {
                    ...state.viewState?.PSTViewStateLayout,
                    Value: value?.id,
                    Label: value?.title,
                },
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGroupBy, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const value = (state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateGrouping?.Options.find(item => item?.id === action.groupBy);
            const vs = {
                ...state.viewState,
                PSTViewStateGrouping: {
                    ...(state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateGrouping,
                    Value: value?.id,
                    Label: value?.title,
                },
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateSortBy, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const value = (state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateSorting?.Options.find(item => item?.id === action.sortBy);
            const vs = {
                ...state.viewState,
                PSTViewStateSorting: {
                    ...(state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateSorting,
                    Value: value?.id,
                    Label: value?.title,
                },
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.toggleGroupSortOder, (state): CreateViewStateState => {
        if (state.viewState) {
            const vs = {
                ...(state.viewState as StorytellingSingleViewStateRecord),
                PSTViewStateGroupSortOrder:
                    (state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.toggleSortOder, (state): CreateViewStateState => {
        if (state.viewState) {
            const vs = {
                ...(state.viewState as StorytellingSingleViewStateRecord),
                PSTViewStateSortOrder: (state.viewState as StorytellingSingleViewStateRecord)?.PSTViewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateLockMapLocation, (state, action): CreateViewStateState => {
        if (isTopicViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingTopicViewStateRecord),
                PSTViewStateMapLockMapLocation: action.locked,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateMapShowTeamLocations, (state, action): CreateViewStateState => {
        if (isTopicViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingTopicViewStateRecord),
                PSTViewStateMapShowTeamLocations: action.show,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateMapZoomToCoverage, (state, action): CreateViewStateState => {
        if (isTopicViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingTopicViewStateRecord),
                PSTViewStateMapZoomToCoverage: action.zoom,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.addViewStateFilters, (state, action): CreateViewStateState => {
        if (isFilteredViewStateRecord(state.viewState)) {
            const vsFilter = { ...(state.viewState as StorytellingSingleViewStateRecord).PSTViewStateFilter };
            vsFilter.Filters = vsFilter.Filters.map(filter => {
                if (action.filters.find(f => f === filter.ID)) {
                    if (action.required) filter = { ...filter, IsRequired: true, IsOptional: false, NotUsed: false };
                    else filter = { ...filter, IsRequired: false, IsOptional: true, NotUsed: false };
                } else {
                    if (action.required) filter = { ...filter, IsRequired: false };
                    else filter = { ...filter, IsOptional: false };
                }
                return filter;
            });
            const vs = {
                ...(state.viewState as StorytellingSingleViewStateRecord),
                PSTViewStateFilter: vsFilter,
            };

            return handleUpdateViewState(state, vs);
        }
    }),
    on(CreateViewStateActions.removeViewStateFilter, (state, action): CreateViewStateState => {
        if (isFilteredViewStateRecord(state.viewState)) {
            const vsFilter = { ...(state.viewState as StorytellingSingleViewStateRecord).PSTViewStateFilter };
            vsFilter.Filters = vsFilter.Filters.map(filter => {
                if (action.filter === filter.ID) {
                    filter = { ...filter, IsRequired: false, IsOptional: false, NotUsed: true };
                }
                return filter;
            });
            const vs = {
                ...(state.viewState as StorytellingSingleViewStateRecord),
                PSTViewStateFilter: vsFilter,
            };

            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFilter, CreateViewStateActions.adjustViewStateFilter, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateFilter = {
                ...vs.PSTViewStateFilter,
                Filters: vs.PSTViewStateFilter.Filters.map(f => {
                    if (f.ID === action.filter.ID) {
                        f = { ...action.filter };
                    }
                    return f;
                }),
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGridOrder, (state, action): CreateViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGridOrder = [...action.columns];
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.removeAllViewStateFilters, (state, action): CreateViewStateState => {
        if (isFilteredViewStateRecord(state.viewState)) {
            const vsFilter = { ...(state.viewState as StorytellingSingleViewStateRecord).PSTViewStateFilter };
            vsFilter.Filters = vsFilter.Filters.map(filter => {
                if (action.required && filter.IsRequired) {
                    filter = { ...filter, IsRequired: false, NotUsed: true };
                } else if (!action.required && filter.IsOptional) {
                    filter = { ...filter, IsOptional: false, NotUsed: true };
                }

                return filter;
            });
            const vs = {
                ...(state.viewState as StorytellingSingleViewStateRecord),
                PSTViewStateFilter: vsFilter,
            };

            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowTopics, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTViewStateShowTopics: action.show,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowAssignments, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTViewStateShowAssignments: action.show,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateShowStories, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTViewStateShowStories: action.show,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateRemoveAddedRecords, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTViewStateRemoveAddedRecords: action.remove,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateGuid, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTViewStateGUID: action.guid,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationChannelGuid, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTPublicationChannelGUID: action.guid,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateEditionName, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTPublicationEditionName: action.name,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationListTitle, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                PSTPublicationListTitle: action.title,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStatePublicationTime, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = state.viewState as StorytellingPublicationViewStateRecord;

            if (vs.PSTViewStateFilter && vs.PSTViewStateFilter.Filters) {
                const filter = { ...vs.PSTViewStateFilter };
                const publicationTime = filter.Filters.find(x => x.ID === PSTFieldNames.PSTPublicationTime);
                if (publicationTime) {
                    publicationTime.NotUsed = false;
                    publicationTime.Value = action.time;
                    vs.PSTViewStateFilter = filter;
                    return handleUpdateViewState(state, vs);
                }
            }
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFeedUrl, (state, action): CreateViewStateState => {
        if (isRSSSFeedViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingRSSViewStateRecord),
                PSTViewStateFeedURL: action.url,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateFeedId, (state, action): CreateViewStateState => {
        if (isSingleFeedViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingSingleFeedViewStateRecord),
                PSTViewStateFeedID: action.id,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateTwitterUsernames, (state, action): CreateViewStateState => {
        if (isTwitterFeedViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingTwitterViewStateRecord),
                PSTViewStateTwitterFeedNames: action.usernames,
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.updateViewStateSequence, (state, action): CreateViewStateState => {
        return {
            ...state,
            selectedViewStates: action.selected,
        };
    }),
    on(CreateViewStateActions.removeAllViewStates, (state, _): CreateViewStateState => {
        if (isMultiFeedViewStateRecord(state.viewState)) {
            const currentSequence = (state.viewState as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence;
            const vs = {
                ...(state.viewState as StorytellingMultiFeedViewStateRecord),
                PSTViewStateSequence: {
                    ...currentSequence,
                    ViewStates: currentSequence.ViewStates.map(item => ({ ...item, Selected: false })),
                },
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.removeViewState, (state, action): CreateViewStateState => {
        if (isMultiFeedViewStateRecord(state.viewState)) {
            const currentSequence = (state.viewState as StorytellingMultiFeedViewStateRecord).PSTViewStateSequence;
            const vs = {
                ...(state.viewState as StorytellingMultiFeedViewStateRecord),
                PSTViewStateSequence: {
                    ...currentSequence,
                    ViewStates: currentSequence.ViewStates.map(item => {
                        if (action.guid === item.Guid) {
                            return { ...item, Selected: false };
                        }
                        return item;
                    }),
                },
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.setStoredViewStates, (state, action): CreateViewStateState => {
        if (isPublicationViewStateRecord(state.viewState)) {
            const vs = {
                ...(state.viewState as StorytellingPublicationViewStateRecord),
                storedViewStates: action.viewStates.filter(x => x.PSTViewStateType === ViewStateTypes.TOPIC || x.PSTViewStateType === ViewStateTypes.ASSIGNMENT),
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(CreateViewStateActions.setApplicationViewStates, (state, action): CreateViewStateState => {
        return { ...state, appViewStates: action.viewStates };
    })
);
